import { lighten } from '@mui/system'
import React from 'react'

import Icon from 'components/Icon'
import { ConcurrentTree } from 'components/scenarios/concurrent/ConcurrentTree'
import {
  ActiveNodeIcon,
  ConcurrentIcon,
  StatusChecksIcon
} from 'components/scenarios/concurrent/ConcurrentTreeIcons'
import { ConcurrentTreeItem } from 'components/scenarios/concurrent/ConcurrentTreeItem'
import { ConcurrentTreeNodeSectionDescription } from 'components/scenarios/concurrent/ConcurrentTreeNodeSectionDescription'
import { ConcurrentTreeNodesRoot } from 'components/scenarios/concurrent/ConcurrentTreeNodesRoot'
import { ExternalActionGraphNode } from 'components/scenarios/concurrent/ExternalActionGraphNode'
import { StatusCheckGraphNode } from 'components/scenarios/concurrent/StatusCheckGraphNode'
import {
  getColorByState,
  getLifecycleColor,
  isActive,
  isFailed,
  isHalted,
  isHaltRequested,
  isNotStarted,
  isSuccessful
} from 'components/scenarios/utils/graph-node-lifecycle'
import {
  NODE_SECTION_MESSAGING,
  type NodeSectionType
} from 'components/scenarios/utils/graph/messaging'
import { isContinuousStatusCheckNode } from 'components/scenarios/utils/graph/nodes/typeguards'
import { muiTheme } from 'mui-theme'
import {
  ScenarioLifecycle,
  type GraphNode,
  type ExternalActionNode,
  type ContinuousStatusCheckNode,
  type RollbackExternalActionNode
} from 'types/api/scenarios/scenario'

const ROW_ICON_STYLE = {
  marginRight: muiTheme.spacing(3),
  marginLeft: muiTheme.spacing(3)
}

type MetaActionNode =
  | ContinuousStatusCheckNode
  | ExternalActionNode
  | RollbackExternalActionNode

/**
 * Find the most relevant lifecycle for the overall list of external actions
 * Goes in order of priority.
 * E.g., if any have halted or failed, then we display an error state
 */
function getOverallMetaActionNodesLifecycle(
  nodes: MetaActionNode[]
): ScenarioLifecycle {
  let lifecycle: ScenarioLifecycle = ScenarioLifecycle.NotStarted

  for (const node of nodes) {
    if (isHalted(node.state?.lifecycle)) {
      lifecycle = ScenarioLifecycle.Halted
      break
    }

    if (isHaltRequested(node.state?.lifecycle)) {
      lifecycle = ScenarioLifecycle.HaltRequested
      break
    }

    if (isFailed(node.state?.lifecycle)) {
      lifecycle = ScenarioLifecycle.Failed
      break
    }

    if (isActive(node.state?.lifecycle)) {
      lifecycle = ScenarioLifecycle.Active
      break
    }

    if (isSuccessful(node.state?.lifecycle)) {
      lifecycle = ScenarioLifecycle.Successful
      break
    }
  }

  return lifecycle
}

export const ConcurrentMetaActionsTreeView: React.FC<{
  nodes: MetaActionNode[]
  sectionType: NodeSectionType
  isClickable?: boolean
  onClick?: (node: GraphNode) => void
}> = ({
  nodes,
  sectionType,
  onClick = () => null,
  isClickable = false,
  ...rest
}) => {
  const lifecycle = getOverallMetaActionNodesLifecycle(nodes)
  const overallStatusColor = getLifecycleColor(lifecycle)
  const overallStatusBgColor = lighten(overallStatusColor, 0.9)

  const { label, description } = NODE_SECTION_MESSAGING[sectionType]

  return (
    <ConcurrentTree
      expanded={['1']} // lock the tree view open at the root node
      {...rest}
    >
      <ConcurrentTreeNodesRoot
        icon={
          <StatusChecksIcon
            css={{
              border: `1px solid ${overallStatusColor}`,
              background: overallStatusBgColor
            }}
          />
        }
        label={label}
        nodeId="1"
      >
        <ConcurrentTreeNodeSectionDescription description={description} />
        {nodes.map((node: MetaActionNode) => {
          const isNodeActive = isActive(node.state?.lifecycle)
          const isNodeNotStarted = isNotStarted(node.state?.lifecycle)
          const statusColor = isNodeNotStarted
            ? getLifecycleColor(ScenarioLifecycle.NotStarted)
            : getColorByState(node)
          const statusBgColor = lighten(statusColor, 0.9)

          const baseNodeProps = {
            isClickable,
            rowActions: isClickable ? (
              <Icon css={ROW_ICON_STYLE} icon="chevronRight" />
            ) : null,
            onClick
          }

          return (
            <ConcurrentTreeItem
              key={node.guid}
              endIcon={
                isNodeActive ? (
                  <ActiveNodeIcon
                    css={{
                      border: `1px solid ${statusColor}`,
                      background: statusBgColor
                    }}
                  />
                ) : (
                  <ConcurrentIcon
                    css={{
                      border: `1px solid ${statusColor}`,
                      background: statusBgColor
                    }}
                  />
                )
              }
              label={
                isContinuousStatusCheckNode(node) ? (
                  <StatusCheckGraphNode {...baseNodeProps} node={node} />
                ) : (
                  <ExternalActionGraphNode {...baseNodeProps} node={node} />
                )
              }
              nodeId={node.guid}
            />
          )
        })}
      </ConcurrentTreeNodesRoot>
    </ConcurrentTree>
  )
}
